import React, { useMemo, useState } from 'react';
import { resolveApiErrorMessage } from '../../../Api';
import Button from '../../../ui/Button';
import GroundLootDialogFrame from '../GroundLoot/GroundLootDialogFrame';
import { createBaseCampOperationId } from './baseCamp';
import './BaseCampScene.css';

function fallbackGameFocus() {
  return document.querySelector('.game-menu__toggle');
}

function travelCopy(request) {
  if (request?.kind === 'camp-enter') {
    return {
      title: 'Enter private Camp?',
      eyebrow: 'Camp travel',
      description: 'Leave the shared region and enter your private Camp.',
      confirmLabel: 'Enter Camp',
    };
  }
  if (request?.kind === 'camp-exit') {
    return {
      title: 'Leave private Camp?',
      eyebrow: 'Return to the world',
      description: 'Return to the shared region at the Camp entrance.',
      confirmLabel: 'Leave Camp',
    };
  }
  if (request?.kind === 'camp-install') {
    const destination = request?.entrance?.regionName || 'this region';
    return {
      title: `Install ${destination} cableway?`,
      eyebrow: 'Permanent Camp route',
      description: 'Pay the full cost from your Pack to permanently connect this regional anchor to Camp.',
      confirmLabel: 'Install cableway',
    };
  }
  if (request?.kind === 'camp-route') {
    const destination = request?.route?.regionName || 'connected region';
    return {
      title: `Travel to ${destination}?`,
      eyebrow: 'Camp cableway',
      description: 'Leave private Camp and arrive at the installed regional anchor.',
      confirmLabel: `Travel to ${destination}`,
    };
  }
  const label = request?.exit?.label || 'connected region';
  return {
    title: `Travel through ${label}?`,
    eyebrow: 'Region travel',
    description: 'Your current movement and gathering route will end when the destination region loads.',
    confirmLabel: `Travel to ${label}`,
  };
}

export default function WorldTravelDialog({
  onClose,
  onConfirm,
  request,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [operationId] = useState(createBaseCampOperationId);
  const copy = useMemo(() => travelCopy(request), [request]);
  const routeCost = request?.kind === 'camp-install'
    ? request.entrance
    : null;
  const shortfall = routeCost
    ? Math.max(
      0,
      Number(routeCost.requiredQuantity || 0)
        - Number(routeCost.availableInPack || 0),
    )
    : 0;

  const confirm = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm?.(operationId);
      onClose?.();
    } catch (travelError) {
      setError(resolveApiErrorMessage(
        travelError,
        'Travel could not be completed. Check your position and try again.',
      ));
    } finally {
      setBusy(false);
    }
  };

  return (
    <GroundLootDialogFrame
      busy={busy}
      className="world-travel-dialog"
      description={copy.description}
      eyebrow={copy.eyebrow}
      getFallbackFocusTarget={fallbackGameFocus}
      onClose={onClose}
      title={copy.title}
    >
      <form className="world-travel-dialog__body" onSubmit={confirm}>
        {request?.kind === 'region-exit' ? (
          <dl className="world-travel-dialog__route">
            <div>
              <dt>Passage</dt>
              <dd>{request.exit.label}</dd>
            </div>
            {request.exit.direction ? (
              <div>
                <dt>Direction</dt>
                <dd>{request.exit.direction}</dd>
              </div>
            ) : null}
          </dl>
        ) : routeCost ? (
          <dl className="world-travel-dialog__route">
            <div>
              <dt>Material</dt>
              <dd>{routeCost.costItemName || routeCost.costItemId}</dd>
            </div>
            <div>
              <dt>In Pack</dt>
              <dd>
                {Number(routeCost.availableInPack || 0)} /{' '}
                {Number(routeCost.requiredQuantity || 0)}
              </dd>
            </div>
            {shortfall > 0 ? (
              <div>
                <dt>Shortfall</dt>
                <dd>{shortfall}</dd>
              </div>
            ) : null}
          </dl>
        ) : (
          <p className="world-travel-dialog__notice">
            Travel is server-confirmed and may take a moment.
          </p>
        )}

        {error ? (
          <p className="ground-loot-dialog__error" role="alert">
            {error}
          </p>
        ) : null}

        <p
          aria-live="polite"
          className="world-travel-dialog__status"
          role="status"
        >
          {busy ? 'Travelling…' : ''}
        </p>

        <div className="ground-loot-dialog__actions">
          <Button disabled={busy} onClick={onClose} variant="secondary">
            Stay here
          </Button>
          <Button
            disabled={busy || shortfall > 0}
            type="submit"
            variant="primary"
          >
            {busy ? 'Travelling…' : copy.confirmLabel}
          </Button>
        </div>
      </form>
    </GroundLootDialogFrame>
  );
}
